import React, { useEffect, useState } from "react";
import Container from "../../components/Container";
import { BsArrowLeft } from "react-icons/bs";
import { PrimaryButton } from "../../components/CustomButton";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { inviteGuest } from "./service/inviteGuest";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ShareInviteLink from "./components/ShareInviteLink";
import { PROTECTED_PATHS } from "../../app/constants";
import ShareModal from "./components/ShareModal";
import CustomModal from "../../components/CustomModal";
import { infoNotifier } from "../../components/NotificationHandler";
import { pickContactFromPhone } from "./service/addFromContact";
import { timeout } from "../../components/utils/timeout";
import PickContactFromPhoneWarning from "./components/PickContactFromPhoneWarning";
import { UserProfile } from "../../context/userProfileContext";
import { COLORS } from "../../constants/colors";
import CustomizeVisitModal from "./components/CustomizeVisitModal";
import "./InviteGuest.css";
import {
  Box,
  Center,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  SlideFade,
  Stack,
  Switch,
  Text,
  useColorModeValue,
  useDisclosure,
  Button,
} from "@chakra-ui/react";

// Import dayjs plugins if needed
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
dayjs.extend(isSameOrAfter);

const InviteGuest = () => {
  const [isOneTimeVisit, setIsOneTimeVisit] = useState("yes");

  const today = new Date();
  const currentTime = dayjs(today).format("HH:mm");
  const appTextColor = useColorModeValue(COLORS.primary, COLORS.white);
  const inputColor = useColorModeValue(COLORS.lightGrey, COLORS.white);
  const btnBg = useColorModeValue(COLORS.primaryDark, COLORS.green);
  const iconColor = useColorModeValue(COLORS.white, COLORS.primary);
  const nameColor = useColorModeValue(COLORS.black, COLORS.primary);
  const { MY_ESTATE } = PROTECTED_PATHS;
  const navigate = useNavigate();
  const url = window?.location.href;
  const urlString = new URL(url);
  const guestName = urlString?.searchParams?.get("name") || "";
  const guestPhone = urlString?.searchParams?.get("phone")?.trim() || "";

  // Default times
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [startTime, setStartTime] = useState(dayjs().format("HH:mm")); // Default to current time in "HH:mm"

  const [endTime, setEndTime] = useState(
    dayjs(today).add(3, "hours").format("HH:mm")
  ); // Default end time is 3 hours after

  const [allowCompany, setAllowCompany] = useState(true);
  const [loading, setLoading] = useState(false);
  const [guestCode, setGuestCode] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [disable, setDisable] = useState(true);
  const [name, setName] = useState(guestName);
  const [phone, setPhone] = useState(guestPhone);
  const { userProfile } = UserProfile();
  const { _id, tenantCode } = userProfile;

  const initialValues = {
    tenantCode,
    userId: _id,
    oneTimeVisit: true ? "yes" : "no",
    allowCompany,
    startDate: dayjs(today).format("YYYY-MM-DD"),
    endDate: dayjs(today).format("YYYY-MM-DD"),
    startTime: dayjs(today).format("HH:mm"),
    endTime: dayjs(today).add(3, "hours").format("HH:mm"),
  };

  const [values, setValues] = useState(initialValues);

  // Handlers
  const handleStartDate = (date) => {
    setStartDate(date);
    const formattedDate = dayjs(date).format("YYYY-MM-DD");

    // If endDate is before the new startDate, update endDate
    if (dayjs(endDate).isBefore(dayjs(date), "day")) {
      setEndDate(date);
      setValues((values) => ({
        ...values,
        endDate: formattedDate,
      }));
    }

    setValues((values) => ({
      ...values,
      startDate: formattedDate,
    }));
  };

  const handleEndDate = (date) => {
    setEndDate(date);
    const formattedDate = dayjs(date).format("YYYY-MM-DD");

    setValues((values) => ({
      ...values,
      endDate: formattedDate,
    }));
  };

  const handleStartTime = (event) => {
    const time = event.target.value;
    setStartTime(time);
    const start = dayjs(`${dayjs(startDate).format("YYYY-MM-DD")} ${time}`);
    const end = start.add(3, "hours");
    const endTimeFormatted = end.format("HH:mm");

    setEndTime(endTimeFormatted);
    setValues((values) => ({
      ...values,
      startTime: time,
      endTime: endTimeFormatted,
    }));
  };

  const handleAllowCompany = () => {
    setAllowCompany(!allowCompany);
    setValues((values) => ({ ...values, allowCompany: !allowCompany }));
  };

  const handleInvite = () => {
    if (disable) {
      infoNotifier("Enter Guest name and Phone number first");
      return;
    }
    const payload = {
      ...values,
      name,
      phone,
      oneTimeVisit: isOneTimeVisit,
    };
    // Log the payload for debugging
    console.log("Payload being sent:", payload);
    inviteGuest(payload, setLoading, setGuestCode, onOpen, onClose);
  };

  const handlepickContactFromPhone = () => {
    pickContactFromPhone(setName, setPhone);
  };

  useEffect(() => {
    name && phone ? setDisable(false) : setDisable(true);
  }, [name, phone]);

  const handleShareClose = () => {
    onClose();
    timeout(() => navigate(MY_ESTATE), 1000);
  };

  return (
    <SlideFade in={true} offsetY="500px">
      <Stack pt="3vh" width="100%" color={appTextColor}>
        <Container>
          <Flex
            py="4"
            mb="5"
            shadow="sm"
            align="center"
            justify="space-between"
          >
            <Box>
              <Link to={PROTECTED_PATHS.GUEST_LIST}>
                <BsArrowLeft size={25} />
              </Link>
            </Box>
            <Text
              flex={0.65}
              fontSize="1.2rem"
              fontWeight="600"
              color={appTextColor}
            >
              Invite Guest
            </Text>
          </Flex>
        </Container>

        <Container>
          <Center py="3">
            <PrimaryButton
              btnText="Add from contact"
              onClick={handlepickContactFromPhone}
              w="100%"
            />
          </Center>
          <HStack>
            <Divider />
            <Text>Or</Text>
            <Divider />
          </HStack>
          <FormControl>
            <Stack spacing="5" pb="5">
              <Box>
                <FormLabel htmlFor="name" mb="0">
                  Guest Name
                </FormLabel>
                <InputGroup
                  bg={COLORS.white}
                  border={`1.5px solid ${COLORS.primary}`}
                  color={iconColor}
                  rounded="lg"
                  mt="5px"
                >
                  <Input
                    id="name"
                    color={nameColor}
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name"
                    _placeholder={{ color: `${COLORS.grey}` }}
                  />
                </InputGroup>
              </Box>

              <Box>
                <FormLabel htmlFor="phone" m="0">
                  Guest Phone Number
                </FormLabel>
                <InputGroup
                  bg={COLORS.white}
                  border={`1.5px solid ${COLORS.primary}`}
                  color={iconColor}
                  rounded="lg"
                  mt="5px"
                >
                  <Input
                    name="phone"
                    id="phone"
                    color={nameColor}
                    value={phone}
                    maxLength={13}
                    onChange={(e) => {
                      const sanitizedPhone = e.target.value.replace(/[^\d]/g, ""); // Remove all non-digit characters
                      if (sanitizedPhone.length === 11 || sanitizedPhone.length <= 13) {
                        setPhone(sanitizedPhone);
                      }
                    }}
                    placeholder="08055... (11 digits) or +234... (13 digits)"
                    _placeholder={{ color: `${COLORS.grey}` }}
                  />
                </InputGroup>
              </Box>
              <Stack spacing="0">
                <Text>Visit Type:</Text>
                <HStack
                  justify="space-between"
                  gap={["0.5rem", "1rem"]}
                  wrap="wrap"
                >
                  <Flex justify="space-between" w="100%">
                    <Button
                      bg={isOneTimeVisit === "yes" ? "#072f64" : "#031730"}
                      padding="8px"
                      borderRadius="4px"
                      size="sm"
                      color="white"
                      fontWeight="bold"
                      w={["140px", "150px"]}
                      _hover={{
                        bg: isOneTimeVisit === "yes" ? "#031730" : "#072f64"
                      }}
                      _focus={{
                        boxShadow: "none",
                      }}
                    >
                      <Checkbox
                        isChecked={isOneTimeVisit === "yes"}
                        onChange={() =>
                          setIsOneTimeVisit(
                            isOneTimeVisit === "no" ? "yes" : "no"
                          )
                        }
                        sx={{
                          color: "#fff",
                          ".chakra-checkbox__control": {
                            borderColor: COLORS.primary,
                            backgroundColor:
                              isOneTimeVisit === "yes"
                                ? "#031730"
                                : "#fff",
                            _hover: {
                              borderColor: COLORS.primary,
                            },
                            _focus: {
                              boxShadow: "none",
                              borderColor: COLORS.primary,
                            },
                          },
                          ".chakra-checkbox__label": {
                            fontWeight: "bold",
                            fontSize: "small",
                          },
                        }}
                      >
                        One Time Visit
                      </Checkbox>
                    </Button>

                    <CustomModal
                      customVisit
                      name={name}
                      phone={phone}
                      startTime={startTime}
                      endTime={endTime}
                      buttonProps={{
                        size: "sm",
                        bg: btnBg,
                        w: ["140px", "150px"],
                      }}
                      btnTitle="Custom Visit"
                    >
                      <CustomizeVisitModal
                        name={name}
                        phone={phone}
                        isChecked={isOneTimeVisit === "no"}
                      />
                    </CustomModal>
                  </Flex>
                </HStack>
              </Stack>

              <Flex justify="space-between" gap="1rem" width="100%">
                <Stack spacing="0" w={"50%"}>
                  <Text>Start Date</Text>
                  <Box
                    bg={COLORS.white} // Set the background color
                    border={`2px solid ${COLORS.primary}`} // Add a solid border
                    borderRadius="0.5rem"
                    color={COLORS.black} // Text color
                    p="5px"
                  >
                    <DatePicker
                      minDate={new Date()}
                      dateFormat={"dd/MM/yyyy"}
                      selected={startDate}
                      onChange={handleStartDate}
                      className="custom-datepicker" // Optionally add custom class for extra styling
                    />
                  </Box>
                </Stack>

                <Stack spacing="0" w={"50%"}>
                  <Text>
                    Start Time(
                    <span style={{ fontWeight: "bold", fontSize: "smaller" }}>
                      Valid for 3 hours
                    </span>
                    )
                  </Text>
                  <Box
                    bg={COLORS.white}
                    border={`1.5px solid ${COLORS.primary}`}
                    borderRadius=".5rem"
                    color={COLORS.grey}
                  >
                    <Input
                      type="time"
                      value={startTime}
                      onChange={handleStartTime}
                    />
                  </Box>
                </Stack>
              </Flex>

              <ShareModal
                size="xs"
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={handleShareClose}
              >
                <ShareInviteLink onClose={onClose} guestCode={guestCode} />
              </ShareModal>
              <PickContactFromPhoneWarning />
            </Stack>
          </FormControl>
          <Flex justify={"space-between"}>
            <Text pr="3">Allow Additional Guest</Text>
            <Switch isChecked={allowCompany} onChange={handleAllowCompany} />
          </Flex>
          <Box textAlign="center" bottom="20">
            <PrimaryButton
              w="100%"
              my="40px"
              onClick={handleInvite}
              isLoading={loading}
              btnText="Send Invite"
            />
          </Box>
        </Container>
      </Stack>
    </SlideFade>
  );
};

export default InviteGuest;
